// src/components/services/Services.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { categories } from './servicePackages';
import "./services.css";


const Services = ({ onSelectPackage, embedded = false, initialCategory, focusPackageId }) => {
  const navigate = useNavigate();
  const categoryKeys = Object.keys(categories);
  const [activeCategory, setActiveCategory] = useState(
    initialCategory && categories[initialCategory] ? initialCategory : categoryKeys[0]
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [quoteVisible, setQuoteVisible] = useState(true);
  const swiperRef = useRef(null);

  const quotes = [
    "Your website is your hardest-working employee — open 24/7, never calls in sick.",
    "People judge your business in 50 milliseconds. Make them count.",
    "If they can't find you online, they'll find your competitor.",
    "A slow site is a closed door. Speed is hospitality.",
    "Digital presence isn't optional anymore. It's survival."
  ];
  const currentQuote = quotes[currentIndex];

  useEffect(() => {
    const FADE = 600; // must match the CSS transition duration
    let swapTimer;
    const interval = setInterval(() => {
      setQuoteVisible(false);
      swapTimer = setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % quotes.length);
        setQuoteVisible(true);
      }, FADE);
    }, 3000);
    return () => {
      clearInterval(interval);
      clearTimeout(swapTimer);
    };
  }, [quotes.length]);

  // Service categories with packages now live in ./servicePackages.js
  // (shared by the calculator + mirrored by the Stripe checkout).

  const handleGetQuote = (service) => {
    // Store selected package in sessionStorage
    const packageData = {
      ...service,
      categoryId: activeCategory,
      category: (categories[activeCategory] || categories[categoryKeys[0]]).title
    };
    
    // Embedded (inside the dashboard): select in place and let the dashboard's
    // calculator + Stripe flow take over — no navigation, no sessionStorage.
    if (onSelectPackage) {
      onSelectPackage(packageData);
      return;
    }

    // Store in sessionStorage for persistence
    sessionStorage.setItem('selectedPackage', JSON.stringify(packageData));
    sessionStorage.setItem('fromServices', 'true');
    
    console.log('Service selected:', packageData);
    
    // Check if user is logged in
    const token = localStorage.getItem('authToken');
    
    if (token) {
      // User is logged in, go directly to dashboard
      navigate('/dashboard', {
        state: {
          selectedPackage: packageData
        }
      });
    } else {
      // Not logged in, go to register
      navigate('/register', {
        state: {
          selectedPackage: packageData,
          redirectTo: '/dashboard'
        }
      });
    }
  };

  const handleCustomPackage = () => {
    const customPackage = {
      id: 'custom',
      categoryId: 'custom',
      name: 'Custom Solution',
      description: 'Tailored to your specific requirements',
      priceValue: 0, // Will be determined after consultation
      isCustom: true,
      features: [
        'Requirements Analysis',
        'Custom Development',
        'Scalable Architecture',
        'Full Documentation',
        'Testing & QA',
        'Ongoing Support'
      ]
    };

    // Embedded (inside the dashboard): select in place.
    if (onSelectPackage) {
      onSelectPackage(customPackage);
      return;
    }

    // Store in sessionStorage
    sessionStorage.setItem('selectedPackage', JSON.stringify(customPackage));
    
    const token = localStorage.getItem('authToken');
    
    if (token) {
      navigate('/dashboard', {
        state: { selectedPackage: customPackage }
      });
    } else {
      navigate('/register', {
        state: { 
          selectedPackage: customPackage,
          redirectTo: '/dashboard'
        }
      });
    }
  };

  // Handle How It Works step clicks
  const handleStepClick = (stepNumber) => {
    if (stepNumber === 1) {
      // Step 1: Choose Service - scroll to services
      const servicesSection = document.getElementById('services');
      if (servicesSection) {
        servicesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // Steps 2, 3, 4: Navigate to login/register
      navigate('/login');
    }
  };

  return (
    <>
    
      <section className={`services section ${embedded ? 'services--embedded' : ''}`} id="services">
        {!embedded && <h2 className="section__title">Services</h2>}
       

        {/* Rotating Marketing Quotes */}
        {!embedded && (
          <div className="marketing-quote">
            <span className={`quote-text ${quoteVisible ? 'is-visible' : 'is-hidden'}`}>{currentQuote}</span>
          </div>
        )}

        {/* Category Tabs */}
        <div className="services__tabs">
          {Object.entries(categories).map(([key, category]) => (
            <button
              key={key}
              className={`services__tab ${activeCategory === key ? 'active' : ''}`}
              onClick={() => setActiveCategory(key)}
            >
              <i className={`uil ${category.icon}`}></i>
              <span>{category.title}</span>
            </button>
          ))}
        </div>

        {/* Services Carousel */}
        <div className="services__container container">
          <Swiper
            onSwiper={(sw) => { swiperRef.current = sw; }}
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={30}
            slidesPerView={1}
            navigation
            pagination={{ clickable: true }}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
            }}
            breakpoints={{
              640: {
                slidesPerView: 2,
              },
              1024: {
                slidesPerView: 3,
              },
            }}
            className="services__swiper"
          >
            {(categories[activeCategory] || categories[categoryKeys[0]]).services.map((service) => (
              <SwiperSlide key={service.id}>
                <div className={`service-card service-card--${service.color}`}>
                  {service.popular && (
                    <span className="popular-badge">Most Popular</span>
                  )}
                  
                  <div className="service-card__header">
                    <h3>{service.name}</h3>
                    <span className="service-card__price">{service.price}</span>
                  </div>
                  
                  <p className="service-card__description">{service.description}</p>
                  
                  {service.savings && (
                    <div className="service-card__savings">
                      💰 {service.savings}
                    </div>
                  )}
                  
                  {service.ideal && (
                    <div className="service-card__ideal">
                      {service.ideal}
                    </div>
                  )}
                  
                  {service.note && (
                    <div className="service-card__note">
                      ℹ️ {service.note}
                    </div>
                  )}
                  
                  <ul className="service-card__features">
                    {service.features.slice(0, 5).map((feature, idx) => (
                      <li key={idx}>
                        <i className="bx bx-check"></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                    {service.features.length > 5 && (
                      <li>
                        <i className="bx bx-plus"></i>
                        <span>And {service.features.length - 5} more features...</span>
                      </li>
                    )}
                  </ul>
                  
                  <div className="service-card__delivery">
                    <i className="bx bx-time"></i>
                    <span>Delivery: {service.delivery}</span>
                  </div>
                  
                  <button 
                    className="service-card__button"
                    onClick={() => handleGetQuote(service)}
                  >
                    Get Free Quote
                    <i className="bx bx-right-arrow-alt"></i>
                  </button>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Custom Package Section */}
        <div className="custom-package-container">
          <div className="custom-package-card">
            <div className="custom-package-icon">
              <i className="uil uil-puzzle-piece"></i>
            </div>
            
            <div className="custom-package-content">
              <h3 className="custom-package-title">Need Something Different?</h3>
              <p className="custom-package-subtitle">
                Request a custom software solution tailored to your specific needs
              </p>
              
              <div className="custom-package-features">
                <div className="custom-feature">
                  <i className="uil uil-check-circle"></i>
                  <span>Custom Web Applications</span>
                </div>
                <div className="custom-feature">
                  <i className="uil uil-check-circle"></i>
                  <span>Mobile App Development</span>
                </div>
                <div className="custom-feature">
                  <i className="uil uil-check-circle"></i>
                  <span>API & Integration Solutions</span>
                </div>
                <div className="custom-feature">
                  <i className="uil uil-check-circle"></i>
                  <span>Enterprise Software</span>
                </div>
              </div>
              
              <p className="custom-package-description">
                Whether you need a complex enterprise system, a unique digital platform, 
                or any specialized software solution - we'll build it exactly to your specifications.
              </p>
            </div>
            
            <div className="custom-package-action">
              <button 
                className="button button--flex custom-quote-btn"
                onClick={handleCustomPackage}
              >
                Request Custom Quote
                <i className="uil uil-arrow-right button__icon"></i>
              </button>
            </div>
          </div>
        </div>

        {/* How It Works + Trust (public page only) */}
        {!embedded && (
        <>
        <div className="how-it-works">
          <h3>💡 How It Works</h3>
          <div className="steps-grid">
            <div className="step-card" onClick={() => handleStepClick(1)}>
              <div className="step-number">1</div>
              <i className="bx bx-mouse"></i>
              <h4>Choose Service</h4>
              <p>Select the package that fits your needs</p>
            </div>
            <div className="step-card" onClick={() => handleStepClick(2)}>
              <div className="step-number">2</div>
              <i className="bx bx-user-plus"></i>
              <h4>Sign Up Free</h4>
              <p>Create your account in 30 seconds</p>
            </div>
            <div className="step-card" onClick={() => handleStepClick(3)}>
              <div className="step-number">3</div>
              <i className="bx bx-customize"></i>
              <h4>Customize</h4>
              <p>Add features and get instant pricing</p>
            </div>
            
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="trust-indicators">
          <div className="trust-item">
            <i className="bx bx-shield-quarter"></i>
            <span>Secure Payments</span>
          </div>
          <div className="trust-item">
            <i className="bx bx-time-five"></i>
            <span>On-Time Delivery</span>
          </div>
          <div className="trust-item">
            <i className="bx bx-support"></i>
            <span>24/7 Support</span>
          </div>
          <div className="trust-item">
            <i className="bx bx-revision"></i>
            <span>Free Revisions</span>
          </div>
        </div>
        </>
        )}
      </section>
    </>
  );
};

export default Services;